import { ErrorHandler, Injectable } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'

import { LaskunAsiakas, LaskunLahetystapa, LaskunTyypit } from '@lemon/core-firebase/model/lasku'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { SahkoisenLaskunValittajaService } from '@lemon/core-firebase/service/lasku/sahkoisen-laskun-valittaja.service'

import { firstValueFrom } from 'rxjs'

import { LaskuService } from '../../_angular/service/lasku/lasku.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'
import { LaskuAsiakasDialog, LaskuAsiakasDialogData, LaskuAsiakasDialogTulos } from '../dialogit/lasku-asiakas.dialog'

@Injectable()
export class AsiakasDialogiService {
  constructor(
    private _dialog: MatDialog,
    private _laskuService: LaskuService,
    private _laskuKopioija: LaskuKopioija,
    private _translationService: LemonTranslationService,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
    private _sahkoisenLaskunValittajaService: SahkoisenLaskunValittajaService,
  ) {}

  async avaaAsiakasrekisterinAsiakas(asiakas: LaskunAsiakas | null) {
    const data: LaskuAsiakasDialogData = {
      asiakas: asiakas,
      esitaytettyNimi: '',
      tyyppi: LaskunTyypit.annaLaskunTyyppi(asiakas?.laskunTyyppi) ?? LaskunTyypit.TAVALLINEN,
      tyyppiMuokattavissa: true,
      valuutta: asiakas?.laskunValuutta ?? 'EUR',
      kieli: asiakas?.laskunKieli ?? 'fi',
      naytaPoista: !!asiakas?.avain,
    }
    const tulos = await firstValueFrom(this._dialog.open<LaskuAsiakasDialog, LaskuAsiakasDialogData, LaskuAsiakasDialogTulos | 'poistettu'>(LaskuAsiakasDialog, { data, width: '880px' }).afterClosed())
    if (!tulos || tulos === 'poistettu') {
      return
    }

    const tallennettava = asiakas ? this._laskuKopioija.copyAsiakas(asiakas) : this._laskuKopioija.annaUusiAsiakas()
    tallennettava.nimi = tulos.nimi
    tallennettava.ytunnus = tulos.ytunnus
    tallennettava.katuosoite = tulos.katuosoite
    tallennettava.postinro = tulos.postinro
    tallennettava.postitmp = tulos.postitoimipaikka
    tallennettava.maa = tulos.maa
    tallennettava.laskunTyyppi = tulos.tyyppi?.avain ?? null
    tallennettava.laskunValuutta = tulos.valuutta
    tallennettava.laskunKieli = tulos.kieli
    tallennettava.oletusLahetystapa = tulos.oletusLahetystapa
    tallennettava.maksuaikaa = tulos.maksuaikaa
    tallennettava.sahkoinenosoite = tulos.sahkoinenosoite
    tallennettava.laskunVastaanottajat = tulos.laskunVastaanottajat
    tallennettava.asiakastyyppi = tulos.asiakastyyppi
    tallennettava.laskutusprofiiliAvain = tulos.laskutusprofiiliAvain

    this._ladataanService.aloitaLataaminen()
    try {
      await this._laskuService.updateAsiakkaat([tallennettava])
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  annaToimitusteksti(asiakas: LaskunAsiakas): string {
    switch (asiakas.oletusLahetystapa) {
      case LaskunLahetystapa.SAHKOINEN: {
        const verkkolasku = this._translationService.lokalisoi('lasku.asiakas-dialog.toimitustapa.verkkolasku')
        const valittajanNimi = this._sahkoisenLaskunValittajaService.annaValittajanNimi(asiakas.sahkoinenosoite?.sahkoinenValittaja)
        return valittajanNimi ? `${verkkolasku} · ${valittajanNimi}` : verkkolasku
      }
      case LaskunLahetystapa.SAHKOPOSTI: {
        const [ensimmainen, ...muut] = asiakas.laskunVastaanottajat ?? []
        if (!ensimmainen) {
          return this._translationService.lokalisoi('lasku.asiakas-dialog.toimitustapa.sahkoposti')
        }
        return muut.length ? `${ensimmainen} +${muut.length}` : ensimmainen
      }
      case LaskunLahetystapa.ITSE:
        return this._translationService.lokalisoi('lasku.asiakas-dialog.toimitustapa.itse')
      default:
        return '–'
    }
  }
}
